// Cap — floating AI assistant. Round button bottom-right that opens a chat panel.
// Positioned to avoid overlap with ScrollToTop (bottom-24 right-6) and the
// centered bottom-nav pill (bottom-4 center).
import { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles, RefreshCw, Loader2, Ruler, ChevronDown, ChevronUp, Copy, Check, Calendar } from "lucide-react";
import api from "@/lib/api";
import { todayIso } from "@/lib/datetime";

function useStockIdFromRoute() {
  // Extract :id when we're on /stock/:id — no rerender-heavy react-router hooks.
  const { pathname } = useLocation();
  const m = pathname.match(/^\/stock\/([^/]+)/);
  return m ? m[1] : null;
}

function newSessionId() {
  return "cap-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 8);
}

function ChatBubble({ role, text }) {
  const isCap = role === "assistant";
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback for older browsers / mobile — create hidden textarea
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* silent */ }
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };
  return (
    <div
      className={`flex ${isCap ? "justify-start" : "justify-end"} group`}
      data-testid={`cap-msg-${role}`}
    >
      <div className="relative max-w-[85%]">
        <div
          className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
            isCap ? "bg-input border border-line text-app" : "font-medium"
          }`}
          style={isCap ? undefined : { backgroundColor: "#00E676", color: "#0A0A0A" }}
        >
          {text}
        </div>
        <button
          type="button"
          onClick={doCopy}
          title={copied ? "Copié" : "Copier"}
          data-testid={`cap-copy-${role}`}
          className={`absolute -top-2 ${isCap ? "-right-2" : "-left-2"} p-1 rounded-full bg-card-2 border border-line-strong text-muted-1 hover:text-app opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity`}
        >
          {copied ? <Check size={12} className="text-invest" /> : <Copy size={12} />}
        </button>
      </div>
    </div>
  );
}

// Échelle — inline calculator: entry price + up/down (either % or raw value)
// → shows final low/high prices and a horizontal scale placing the entry
// between them. Purely local math, no LLM call. Iter 27.
function EchelleTool({ open, onToggle }) {
  const [price, setPrice] = useState("");
  const [downMode, setDownMode] = useState("pct"); // "pct" | "val"
  const [downValue, setDownValue] = useState("");
  const [upMode, setUpMode] = useState("pct");
  const [upValue, setUpValue] = useState("");

  // Robust decimal parser: accepts both "2.50" and "2,50" (French locale) and
  // strips any thousand separators / spaces. parseFloat("2,50")===2 was the
  // culprit that made the Échelle appear to ignore decimals for users typing
  // with a comma. Iter 28.4 fix.
  const parseNum = (s) => {
    if (s == null) return 0;
    const cleaned = String(s).replace(/\s/g, "").replace(",", ".");
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
  };
  const p = parseNum(price);
  const dv = parseNum(downValue);
  const uv = parseNum(upValue);
  // Symmetric input logic:
  //  - "pct" mode: user typed a percentage → final = base × (1 ± pct/100)
  //  - "$"   mode: user typed a DELTA in dollars → final = base ± delta.
  //    Example: base=2,50, Hausse=$5 → final = 7,50 (price + $5), %=(5/2.50)*100=+200%.
  //  Both the entered value AND its derived counterpart are always displayed.
  const low  = p > 0 ? (downMode === "pct" ? p * (1 - dv / 100) : p - dv) : 0;
  const high = p > 0 ? (upMode   === "pct" ? p * (1 + uv / 100) : p + uv) : 0;
  const dPct = downMode === "pct" ? dv : (p > 0 && dv > 0 ? (dv / p) * 100 : 0);
  const uPct = upMode   === "pct" ? uv : (p > 0 && uv > 0 ? (uv / p) * 100 : 0);
  const range = Math.max(0, high - low);
  const relPos = range > 0 ? ((p - low) / range) * 100 : 50;

  return (
    <div className="border-b" style={{ borderColor: "var(--border)" }} data-testid="cap-echelle-block">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-4 py-2 text-xs text-app hover:bg-card-2 transition-colors"
        data-testid="cap-echelle-toggle"
      >
        <Ruler size={13} className="text-invest" />
        <span className="font-medium">Échelle</span>
        <span className="text-[10px] text-muted-2 ml-1">calcul de niveaux</span>
        <span className="ml-auto">{open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
      </button>
      {open && (
        <div className="px-4 py-3 space-y-3 bg-card-2/40" data-testid="cap-echelle-panel">
          {/* Top: current price snapshot */}
          <div className="flex items-baseline gap-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-2">Prix</div>
            <input
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="ex : 12.50"
              inputMode="decimal"
              className="flex-1 bg-input border border-line-strong rounded-md px-2 py-1 font-mono-num text-lg text-app placeholder:text-muted-2 focus:outline-none focus:border-invest"
              data-testid="cap-echelle-price"
            />
            <span className="font-mono-num text-lg text-invest">$</span>
          </div>

          {/* Baisse + Hausse rows */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-widest text-ignore">Baisse</div>
              <div className="flex items-center gap-1.5">
                <input
                  value={downValue}
                  onChange={(e) => setDownValue(e.target.value)}
                  placeholder="0"
                  inputMode="decimal"
                  className="w-full bg-input border border-line rounded-md px-2 py-1 font-mono-num text-sm text-app focus:outline-none focus:border-ignore"
                  data-testid="cap-echelle-down"
                />
                <button
                  onClick={() => setDownMode(downMode === "pct" ? "val" : "pct")}
                  className="text-[10px] px-1.5 py-1 rounded border border-line text-muted-1 hover:text-app min-w-[28px]"
                  title="Basculer % / $"
                >{downMode === "pct" ? "%" : "$"}</button>
              </div>
              <div className="font-mono-num text-sm text-ignore" data-testid="cap-echelle-low">
                → {low > 0 ? low.toFixed(4).replace(/\.?0+$/, "") : "—"} $ <span className="text-[10px] text-muted-2">(-{dPct.toFixed(2)}%)</span>
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-widest text-invest">Hausse</div>
              <div className="flex items-center gap-1.5">
                <input
                  value={upValue}
                  onChange={(e) => setUpValue(e.target.value)}
                  placeholder="0"
                  inputMode="decimal"
                  className="w-full bg-input border border-line rounded-md px-2 py-1 font-mono-num text-sm text-app focus:outline-none focus:border-invest"
                  data-testid="cap-echelle-up"
                />
                <button
                  onClick={() => setUpMode(upMode === "pct" ? "val" : "pct")}
                  className="text-[10px] px-1.5 py-1 rounded border border-line text-muted-1 hover:text-app min-w-[28px]"
                  title="Basculer % / $"
                >{upMode === "pct" ? "%" : "$"}</button>
              </div>
              <div className="font-mono-num text-sm text-invest" data-testid="cap-echelle-high">
                → {high > 0 ? high.toFixed(4).replace(/\.?0+$/, "") : "—"} $ <span className="text-[10px] text-muted-2">(+{uPct.toFixed(2)}%)</span>
              </div>
            </div>
          </div>

          {/* Bottom: horizontal scale */}
          {p > 0 && range > 0 && (
            <div className="pt-1">
              <div className="relative h-1.5 rounded-full overflow-hidden" style={{ background: "linear-gradient(to right, #FF3547 0%, #666 50%, #00E676 100%)" }}>
                <div
                  className="absolute -top-0.5 w-2.5 h-2.5 rounded-full bg-app border-2"
                  style={{ left: `calc(${Math.max(0, Math.min(100, relPos))}% - 5px)`, borderColor: "#0A0A0A" }}
                />
              </div>
              <div className="flex justify-between text-[10px] font-mono-num mt-1.5">
                <span className="text-ignore">
                  {low.toFixed(4).replace(/\.?0+$/, "")} $
                  <span className="text-muted-2 ml-1">(-{dPct.toFixed(2)}%)</span>
                </span>
                <span className="text-muted-1">{p.toFixed(4).replace(/\.?0+$/, "")} $</span>
                <span className="text-invest">
                  {high.toFixed(4).replace(/\.?0+$/, "")} $
                  <span className="text-muted-2 ml-1">(+{uPct.toFixed(2)}%)</span>
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Résumé du jour — inline day picker + button that asks Cap for a rich AI
// summary of a trading day (including counterfactual strategy comparison).
// Iter 28.2.
function DaySummaryTool({ open, onToggle, sessionId, onAppend, disabled }) {
  const [date, setDate] = useState(() => todayIso());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const request = async () => {
    if (busy || disabled) return;
    setBusy(true);
    setErr(null);
    try {
      // Echo the user intent in the chat log first so the request is traceable
      onAppend({ role: "user", text: `Résumé factuel de ma journée du ${date}.` });
      const r = await api.post("/cap/day-summary", {
        session_id: sessionId,
        trade_date: date,
      });
      onAppend({ role: "assistant", text: r.data.reply });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Requête échouée.";
      setErr(msg);
      onAppend({ role: "assistant", text: "Erreur : " + msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-b" style={{ borderColor: "var(--border)" }} data-testid="cap-daysummary-block">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-4 py-2 text-xs text-app hover:bg-card-2 transition-colors"
        data-testid="cap-daysummary-toggle"
      >
        <Calendar size={13} className="text-invest" />
        <span className="font-medium">Résumé du jour</span>
        <span className="text-[10px] text-muted-2 ml-1">IA · avec comparaison de stratégies</span>
        <span className="ml-auto">{open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
      </button>
      {open && (
        <div className="px-4 py-3 space-y-2.5 bg-card-2/40" data-testid="cap-daysummary-panel">
          <div className="flex items-center gap-2">
            <label className="text-[10px] uppercase tracking-widest text-muted-2 shrink-0">Jour</label>
            <input
              type="date"
              value={date}
              max={todayIso()}
              onChange={(e) => setDate(e.target.value)}
              className="flex-1 bg-input border border-line-strong rounded-lg px-2.5 py-1.5 text-xs font-mono-num text-app focus:outline-none focus:border-invest"
              data-testid="cap-daysummary-date"
            />
          </div>
          <button
            type="button"
            onClick={request}
            disabled={busy || disabled}
            className="w-full py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-2 transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-40"
            style={{
              background: "linear-gradient(135deg, #00E676, #00B0FF)",
              color: "#0A0A0A",
            }}
            data-testid="cap-daysummary-run"
          >
            {busy ? (
              <>
                <Loader2 size={12} className="animate-spin" /> Cap analyse ta journée…
              </>
            ) : (
              <>
                <Sparkles size={12} /> Générer le résumé
              </>
            )}
          </button>
          {err && (
            <div className="text-[11px] text-ignore" data-testid="cap-daysummary-err">
              {err}
            </div>
          )}
          <div className="text-[10px] text-muted-2 leading-relaxed">
            Cap compare la P&amp;L réelle à la P&amp;L qu&apos;aurait rapporté l&apos;autre
            stratégie sur les mêmes titres, puis met en avant les actions marquantes.
            Le résumé est sauvegardé dans cette conversation.
          </div>
        </div>
      )}
    </div>
  );
}

export default function CapAssistant() {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(
    () => localStorage.getItem("cap_disabled") !== "1"
  );
  const [sessionId, setSessionId] = useState(() =>
    localStorage.getItem("cap_session") || newSessionId()
  );
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [quickActions, setQuickActions] = useState([]);
  const [echelleOpen, setEchelleOpen] = useState(false);
  const [daySumOpen, setDaySumOpen] = useState(false);
  const scrollRef = useRef(null);
  const stockId = useStockIdFromRoute();

  useEffect(() => {
    localStorage.setItem("cap_session", sessionId);
  }, [sessionId]);

  // React to setting changes (from Settings page or another tab)
  useEffect(() => {
    const sync = () => {
      const disabled = localStorage.getItem("cap_disabled") === "1";
      setEnabled(!disabled);
      if (disabled) setOpen(false);
    };
    window.addEventListener("storage", sync);
    window.addEventListener("cap-setting-changed", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("cap-setting-changed", sync);
    };
  }, []);

  // Load prior history + quick actions when opening
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [hist, qa] = await Promise.all([
          api.get(`/cap/history/${encodeURIComponent(sessionId)}`),
          api.get("/cap/quick-actions"),
        ]);
        if (cancelled) return;
        setMessages(hist.data || []);
        setQuickActions(qa.data || []);
      } catch {
        /* silent */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, sessionId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const send = async (rawText) => {
    const text = (rawText ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    // Optimistic user echo
    setMessages((m) => [...m, { role: "user", text }]);
    try {
      const r = await api.post("/cap/chat", {
        session_id: sessionId,
        message: text,
        stock_id: stockId,
      });
      setMessages((m) => [...m, { role: "assistant", text: r.data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text:
            "Erreur : " +
            (e?.response?.data?.detail || e?.message || "impossible de contacter Cap."),
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const resetConversation = async () => {
    try {
      await api.delete(`/cap/history/${encodeURIComponent(sessionId)}`);
    } catch {
      /* ignore */
    }
    const s = newSessionId();
    setSessionId(s);
    setMessages([]);
  };

  return (
    <>
      {/* Floating trigger — bottom-24 (higher than nav pill, opposite side of scroll-to-top which is now on the left) */}
      {enabled && !open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Ouvrir Cap l'assistant"
          data-testid="cap-open-btn"
          className="fixed z-40 right-4 sm:right-6 bottom-24 w-14 h-14 rounded-full flex items-center justify-center shadow-2xl transition-transform hover:scale-105 active:scale-95"
          style={{
            background: "linear-gradient(135deg, #00E676 0%, #00B0FF 100%)",
            color: "#0A0A0A",
          }}
        >
          <MessageCircle size={22} strokeWidth={2.5} />
          <span
            className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center"
            style={{ background: "#0A0A0A", color: "#00E676" }}
          >
            C
          </span>
        </button>
      )}

      {/* Panel — bottom sheet on mobile (~60vh), side panel on desktop */}
      {enabled && open && (
        <div
          className="fixed z-50 top-[22vh] bottom-4 left-3 right-3 sm:inset-auto sm:right-4 sm:bottom-4 sm:top-4 sm:left-auto sm:w-[420px] flex flex-col"
          data-testid="cap-panel"
        >
          <div
            className="flex-1 flex flex-col rounded-2xl border overflow-hidden shadow-2xl"
            style={{
              backgroundColor: "var(--card)",
              borderColor: "var(--border-strong)",
            }}
          >
            {/* Header */}
            <div
              className="flex items-center gap-3 px-4 py-3 border-b"
              style={{ borderColor: "var(--border)" }}
            >
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center"
                style={{
                  background: "linear-gradient(135deg, #00E676, #00B0FF)",
                  color: "#0A0A0A",
                }}
              >
                <Sparkles size={16} strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-app text-sm">
                  Cap
                  {stockId && (
                    <span className="ml-2 text-[10px] font-mono-num text-invest uppercase tracking-widest">
                      · contexte stock actif
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-muted-1">
                  Assistant GeniCap · français · factuel
                </div>
              </div>
              <button
                onClick={resetConversation}
                title="Nouvelle conversation"
                className="text-muted-1 hover:text-app transition-colors p-1.5 rounded-md hover:bg-card-2"
                data-testid="cap-reset-btn"
              >
                <RefreshCw size={15} />
              </button>
              <button
                onClick={() => setOpen(false)}
                aria-label="Fermer Cap"
                data-testid="cap-close-btn"
                className="text-muted-1 hover:text-app transition-colors p-1.5 rounded-md hover:bg-card-2"
              >
                <X size={17} />
              </button>
            </div>

            {/* Échelle — inline calculator (iter 27, before quick actions) */}
            <EchelleTool open={echelleOpen} onToggle={() => setEchelleOpen((v) => !v)} />

            {/* Résumé du jour — AI summary with strategy counterfactual (iter 28.2) */}
            <DaySummaryTool
              open={daySumOpen}
              onToggle={() => setDaySumOpen((v) => !v)}
              sessionId={sessionId}
              onAppend={(m) => setMessages((prev) => [...prev, m])}
              disabled={busy}
            />

            {/* Quick actions */}
            {quickActions.length > 0 && (
              <div
                className="flex gap-1.5 px-3 py-2 border-b overflow-x-auto no-scrollbar"
                style={{ borderColor: "var(--border)" }}
              >
                {quickActions.map((qa) => (
                  <button
                    key={qa.code}
                    onClick={() => send(qa.prompt_fr)}
                    disabled={busy}
                    className="shrink-0 text-[11px] px-2.5 py-1.5 rounded-full border border-line text-muted-1 hover:text-app hover:border-line-strong transition-colors disabled:opacity-40"
                    data-testid={`cap-quick-${qa.code}`}
                  >
                    {qa.label}
                  </button>
                ))}
              </div>
            )}

            {/* Messages */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto p-3 space-y-3"
              data-testid="cap-messages"
            >
              {messages.length === 0 && (
                <div className="text-center py-8 space-y-3">
                  <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center"
                    style={{
                      background: "linear-gradient(135deg, #00E67633, #00B0FF33)",
                      border: "1px solid var(--border-strong)",
                    }}
                  >
                    <Sparkles size={22} className="text-invest" />
                  </div>
                  <div className="text-sm font-display text-app">Bonjour, je suis Cap.</div>
                  <p className="text-[11px] text-muted-1 max-w-xs mx-auto leading-relaxed">
                    Pose-moi une question sur une action (prix live, pré-market,
                    clôture), un résumé de ta journée, ou une analyse factuelle.
                    Aucun conseil d'achat/vente.
                  </p>
                </div>
              )}
              {messages.map((m, i) => (
                <ChatBubble key={i} role={m.role} text={m.text} />
              ))}
              {busy && (
                <div className="flex justify-start" data-testid="cap-busy">
                  <div className="bg-input border border-line rounded-2xl px-3.5 py-2.5 text-xs text-muted-1 flex items-center gap-2">
                    <Loader2 size={12} className="animate-spin" />
                    Cap réfléchit…
                  </div>
                </div>
              )}
            </div>

            {/* Composer */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-end gap-2 p-3 border-t"
              style={{ borderColor: "var(--border)" }}
            >
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder={
                  stockId
                    ? "Ex : Quel est le prix live et sa progression ?"
                    : "Pose ta question à Cap…"
                }
                rows={1}
                className="flex-1 resize-none bg-input border border-line-strong rounded-xl px-3 py-2 text-sm font-mono-num text-app placeholder:text-muted-2 focus:outline-none focus:border-invest max-h-32"
                data-testid="cap-input"
                disabled={busy}
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                className="w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-40 transition-transform hover:scale-105 active:scale-95"
                style={{
                  background: "linear-gradient(135deg, #00E676, #00B0FF)",
                  color: "#0A0A0A",
                }}
                data-testid="cap-send-btn"
                aria-label="Envoyer"
              >
                <Send size={16} />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
